/**
 * @file Notifications.jsx
 * @description Notification bell and dropdown panel. Displays unread notification
 *   count, recent notifications list, and a job alerts modal for therapists.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/Notifications
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { useIsMobile } from "../hooks/useIsMobile";
import { supabase } from "../lib/supabase";
import { fetchNotifications, markAllNotificationsRead, markNotificationRead, markNotificationUnread, deleteNotification, normalizeNotification } from "../lib/notificationService";
import { BellIcon, BellOffIcon, MapPinIcon, MessageIcon, UserIcon, UsersIcon, ClipboardIcon, CheckIcon, SettingsIcon, CalendarIcon, AlertTriangleIcon, XIcon } from "./Icons";

// ─── Notification helpers ─────────────────────────────────────────────────────
function NotifIcon({ type, size = 16 }) {
  switch (type) {
    case "new_match":          return <MapPinIcon size={size} />;
    case "team_joined":        return <UsersIcon size={size} />;
    case "agency_reply":       return <MessageIcon size={size} />;
    case "new_applicant":           return <UserIcon size={size} />;
    case "application_update":      return <ClipboardIcon size={size} />;
    case "application_reviewing":   return <ClipboardIcon size={size} />;
    case "application_interview":   return <CalendarIcon size={size} />;
    case "application_offered":     return <CheckIcon size={size} />;
    case "application_declined":    return <AlertTriangleIcon size={size} />;
    case "credential_expiring":     return <AlertTriangleIcon size={size} />;
    case "interview_reminder":      return <CalendarIcon size={size} />;
    case "interview_invite_sent":   return <CalendarIcon size={size} />;
    case "interview_confirmed":     return <CheckIcon size={size} />;
    case "interview_response_received": return <CalendarIcon size={size} />;
    case "interview_cancelled":     return <AlertTriangleIcon size={size} />;
    default:                        return <BellIcon size={size} />;
  }
}

const NOTIF_PAGE = 20; // rows per fetch in the bell; "Show older" pages backwards

function relativeTime(iso, t) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hrs   = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 2)   return t("relativeTime.justNow");
  if (mins < 60)  return t("relativeTime.minutesAgo", { count: mins });
  if (hrs  < 24)  return t("relativeTime.hoursAgo", { count: hrs });
  if (days === 1) return t("relativeTime.yesterday");
  if (days < 7)   return t("relativeTime.daysAgo", { count: days });
  return t("relativeTime.weeksAgo", { count: Math.floor(days / 7) });
}

// ─── Job Alerts Modal (slim redirect) ─────────────────────────────────────────
// Full configuration is now in Notification Settings (view="notifications").
// This component is kept for backward compatibility but only shows a status
// summary and a shortcut to Notification Settings.
export function JobAlertsModal({ open, onClose, onGoToSettings }) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(15,23,42,0.40)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: T.surface,
        borderRadius: T.radiusLg,
        width: isMobile ? "calc(100% - 32px)" : 400,
        boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${T.border}`,
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: T.gradientBlue, pointerEvents: "none",
          }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{
                width: 34, height: 34, borderRadius: T.radiusMd,
                background: T.blueLight, border: `1px solid ${T.blue}22`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, color: T.blue,
              }}><BellIcon size={16} color="currentColor" /></div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t("notifications.jobAlertsHeader")}</div>
            </div>
            <button onClick={onClose} style={{
              background: T.surfaceAlt, border: `1px solid ${T.border}`,
              borderRadius: "50%", width: 28, height: 28, cursor: "pointer",
              fontSize: 12, color: T.textMid,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 24 }}>

        {/* Info card */}
        <div style={{
          marginBottom: 20, padding: "14px 16px", borderRadius: 10,
          background: T.blueLight, border: `1px solid ${T.blue}33`,
          display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <SettingsIcon size={16} color={T.blue} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.blue, marginBottom: 4 }}>
              {t("notifications.jobAlertsMovedTitle")}
            </div>
            <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.5 }}>
              {t("notifications.jobAlertsMovedBody")}
            </div>
          </div>
        </div>

        {/* Go to settings button */}
        <button
          onClick={() => { if (onGoToSettings) onGoToSettings(); else onClose(); }}
          style={{
            width: "100%", padding: "11px", borderRadius: T.radius,
            background: T.blue, border: "none",
            color: "#fff", fontSize: 13, fontWeight: 700,
            fontFamily: T.fontBody, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          <SettingsIcon size={14} color="#fff" /> {t("notifications.openSettings")}
        </button>

        {/* secondary close */}
        <button onClick={onClose} style={{
          width: "100%", marginTop: 10, padding: "9px", borderRadius: T.radius,
          background: "none", border: `1px solid ${T.border}`,
          color: T.textMid, fontSize: 12, fontWeight: 600,
          fontFamily: T.fontBody, cursor: "pointer",
        }}>
          {t("common.close")}
        </button>

        </div>{/* end padding div */}
      </div>
    </div>
  );
}


export function applyNotificationClick({ notifs, notification, userId, setNotifs, setNotifError, setView, setOpen, markRead, onOpenInboxThread, onOpenApplicationThread, onOpenApplicantCard, onHighlightMatchCase, markReadErrorMessage }) {
  const prevNotifs = notifs;
  setNotifError("");
  setNotifs(prev => prev.map(x => x.id === notification.id ? { ...x, read: true } : x));
  if (!notification.read && userId) {
    markRead(notification.id)
      .catch(() => {
        setNotifs(prevNotifs);
        setNotifError(markReadErrorMessage || "Could not update notification status. Please try again.");
      });
  }
  if (notification.linkView) {
    // Remap views that were removed from top-level routing into the dashboard
    const VIEW_REMAP = { applicants: "dashboard", posting: "dashboard", therapists: "dashboard" };
    const targetView = VIEW_REMAP[notification.linkView] || notification.linkView;

    if (targetView === "inbox" && notification.linkThreadId && onOpenInboxThread) {
      onOpenInboxThread(notification.linkThreadId);
    } else if (targetView === "applications" && notification.linkThreadId && onOpenApplicationThread) {
      // Deep-link: navigate to My Applications and auto-expand the specific card
      onOpenApplicationThread(notification.linkThreadId);
    } else if (targetView === "dashboard" && (notification.linkApplicationId || notification.linkCaseId) && onOpenApplicantCard) {
      // Deep-link: navigate to agency dashboard pipeline and highlight a
      // target. If applicationId is present, the applicant card is the
      // anchor (existing application_status_changed flow). If only
      // caseId is present — the canonical case for therapist_deleted /
      // case-level notifications where the applicant row is anonymized
      // or no longer relevant — the listing card itself is highlighted
      // and the right panel lands on that case's queue with case
      // context intact.
      onOpenApplicantCard({ applicationId: notification.linkApplicationId, caseId: notification.linkCaseId });
    } else if (targetView === "matches" && notification.linkCaseId && onHighlightMatchCase) {
      // Direct Invite — open Best Matches and highlight the invited card.
      // App.js owns the highlight state and clears it after RBTProfile's
      // own timer fades the gold border out.
      onHighlightMatchCase(notification.linkCaseId);
    } else {
      setView(targetView);
    }
    setOpen(false);
  }
}

// ─── Notification Bell ────────────────────────────────────────────────────────
export function NotificationBell({ userRole, setView, onOpenJobAlerts, asPill, userId, onOpenInboxThread, onOpenApplicationThread, onOpenApplicantCard, onHighlightMatchCase }) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [notifs, setNotifs]           = useState([]);
  const [open, setOpen]               = useState(false);
  const [notifError, setNotifError]   = useState("");
  // "Show older": the bell loads NOTIF_PAGE at a time and pages backwards, so
  // everything retention kept (Pro/founder agencies: full history; everyone
  // else: 30 days) is reachable. No client-side age/plan filter: the database
  // purge (purge_notifications_by_plan) is the single source of that policy.
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const ref                           = useRef(null);
  const hasLoadedRef                  = useRef(false); // prevent re-fetch on every bell open
  // Keep the latest `t` in a ref so the realtime callback can format times in
  // the current language WITHOUT putting `t` in the subscription effect's deps.
  // `t` changes identity on language switch; if it were a dep, the effect would
  // tear down + re-subscribe on the SAME channel name, and the synchronous
  // re-subscribe collides with the not-yet-removed channel ("cannot add
  // postgres_changes callbacks ... after subscribe()").
  const tRef                          = useRef(t);
  tRef.current = t;

  const unread = notifs.filter(n => !n.read).length;

  // Load notifications from Supabase — only once per userId.
  // Realtime subscription (below) keeps the list current after the initial load.
  useEffect(() => {
    if (!userId) return;
    if (hasLoadedRef.current) return; // already loaded; realtime handles new arrivals
    hasLoadedRef.current = true;
    setNotifError("");
    fetchNotifications(userId, NOTIF_PAGE)
      .then((data) => {
        setNotifs(data.map(n => ({ ...n, createdAt: n.time, time: relativeTime(n.time, t) })));
        setHasMore(data.length >= NOTIF_PAGE);
      })
      .catch(() => {
        setNotifError(t("notifications.errorLoad"));
      });
  }, [userId, t]);

  // Realtime notification updates
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notifications:user:${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          const normalized = normalizeNotification(payload.new);
          setNotifs(prev => {
            if (prev.some(n => n.id === normalized.id)) return prev;
            // No slice: older pages the user has loaded via "Show older" must stay.
            return [{ ...normalized, createdAt: normalized.time, time: relativeTime(normalized.time, tRef.current) }, ...prev];
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          const normalized = normalizeNotification(payload.new);
          setNotifs(prev => {
            const current = prev.find(n => n.id === normalized.id);
            const updated = { ...current, ...normalized, createdAt: normalized.time, time: relativeTime(normalized.time, tRef.current) };
            // Re-ring (a new message in a thread whose notification was already
            // read): the server re-arms the SAME row with created_at = now().
            // Move it to the top so it reads as the newest item. A plain
            // read/unread toggle leaves created_at alone and keeps its place.
            if (current && current.createdAt && new Date(normalized.time) > new Date(current.createdAt)) {
              return [updated, ...prev.filter(n => n.id !== normalized.id)];
            }
            return prev.map(n => n.id === normalized.id ? updated : n);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // NOT `t` — see tRef above. Re-subscribing on language change collided
    // on the static channel name and crashed the inbox/notification surface.
  }, [userId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const [hoveredId, setHoveredId] = useState(null);

  const loadOlder = () => {
    const oldest = notifs[notifs.length - 1];
    if (!userId || !oldest?.createdAt || loadingMore) return;
    setLoadingMore(true);
    setNotifError("");
    fetchNotifications(userId, NOTIF_PAGE, oldest.createdAt)
      .then((data) => {
        setNotifs(prev => {
          const seen = new Set(prev.map(n => n.id));
          const fresh = data.filter(n => !seen.has(n.id))
            .map(n => ({ ...n, createdAt: n.time, time: relativeTime(n.time, tRef.current) }));
          return [...prev, ...fresh];
        });
        setHasMore(data.length >= NOTIF_PAGE);
      })
      .catch(() => setNotifError(tRef.current("notifications.errorLoad")))
      .finally(() => setLoadingMore(false));
  };

  const markAllRead = () => {
    const prevNotifs = notifs;
    setNotifError("");
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    if (userId) {
      markAllNotificationsRead(userId)
        .catch(() => {
          setNotifs(prevNotifs);
          setNotifError(t("notifications.errorMarkAllRead"));
        });
    }
  };

  const handleToggleRead = (e, n) => {
    e.stopPropagation();
    const prevNotifs = notifs;
    setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: !n.read } : x));
    (n.read ? markNotificationUnread(n.id) : markNotificationRead(n.id))
      .catch(() => setNotifs(prevNotifs));
  };

  const handleDelete = (e, n) => {
    e.stopPropagation();
    setNotifs(prev => prev.filter(x => x.id !== n.id));
    deleteNotification(n.id).catch(() => {
      setNotifs(prev => {
        if (prev.some(x => x.id === n.id)) return prev;
        return [n, ...prev].sort((a, b) => new Date(b.time) - new Date(a.time));
      });
    });
  };

  const handleDeleteAll = () => {
    const prevNotifs = notifs;
    setNotifs([]);
    Promise.all(prevNotifs.map(n => deleteNotification(n.id)))
      .catch(() => setNotifs(prevNotifs));
  };

  const handleClick = (n) => applyNotificationClick({
    notifs,
    notification: n,
    userId,
    setNotifs,
    setNotifError,
    setView,
    setOpen,
    markRead: markNotificationRead,
    onOpenInboxThread,
    onOpenApplicationThread,
    onOpenApplicantCard,
    onHighlightMatchCase,
    markReadErrorMessage: t("notifications.errorMarkRead"),
  });

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger — pill style OR classic circle bell */}
      {asPill ? (
        <button
          onClick={() => setOpen(p => !p)}
          style={{
            position: "relative",
            display: "flex", alignItems: "center", gap: 6,
            padding: "5px 14px", borderRadius: T.radiusFull,
            border: `1px solid ${open ? T.blue + "66" : T.border}`,
            background: open ? T.blueLight : T.surface,
            cursor: "pointer", fontSize: 12, fontWeight: 500,
            color: open ? T.blue : T.textMid,
            fontFamily: "'Inter', 'DM Sans', sans-serif",
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (!open) { e.currentTarget.style.background = T.surfaceAlt; e.currentTarget.style.borderColor = T.blue + "44"; } }}
          onMouseLeave={e => { if (!open) { e.currentTarget.style.background = T.surface;    e.currentTarget.style.borderColor = T.border; } }}
        >
          <BellIcon size={14} />
          {t("notifications.alertsButton")}
          {unread > 0 && (
            <span style={{
              minWidth: 18, height: 18, borderRadius: 10,
              background: T.orange, color: "#fff",
              fontSize: 10, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 5px", lineHeight: 1,
            }}>{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      ) : (
        <button onClick={() => setOpen(p => !p)} style={{
          position: "relative", minWidth: isMobile ? 44 : 36, minHeight: isMobile ? 44 : 36,
          width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, borderRadius: "50%",
          background: open ? T.blueLight : "transparent",
          border: `1px solid ${open ? T.blue + "44" : "transparent"}`,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.15s",
        }}>
          <BellIcon size={20} />
          {unread > 0 && (
            <span style={{
              position: "absolute", top: 3, right: 3,
              background: T.orange, color: "#fff",
              borderRadius: 20, fontSize: 9, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              padding: "1px 4px", minWidth: 14, textAlign: "center",
              lineHeight: "14px", border: "2px solid #fff",
            }}>{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      )}

      {/* Dropdown panel */}
      {open && (
        <div style={isMobile ? {
          position: "fixed", top: 56, left: 0, right: 0,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderTop: "none",
          boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
          zIndex: 1097, overflow: "hidden",
          animation: "dropIn 0.14s cubic-bezier(0.4, 0, 0.2, 1)",
        } : {
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 348, background: T.surface,
          border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
          boxShadow: "0 16px 48px rgba(15,23,42,0.14), 0 4px 12px rgba(15,23,42,0.06)",
          zIndex: 100, overflow: "hidden",
          animation: "dropIn 0.14s cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px 10px", borderBottom: `1px solid ${T.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
              {t("notifications.header")} {unread > 0 && (
                <span style={{ marginLeft: 6, fontSize: 11, background: T.orangeLight, color: T.orange, padding: "1px 7px", borderRadius: 20, fontFamily: "'JetBrains Mono', monospace" }}>
                  {unread} new
                </span>
              )}
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {unread > 0 && (
                <button onClick={markAllRead} style={{
                  fontSize: 11, color: T.blue, background: "none", border: "none",
                  cursor: "pointer", fontFamily: "'Inter', 'DM Sans', sans-serif", fontWeight: 600,
                }}>{t("notifications.markAllRead")}</button>
              )}
              {notifs.length > 0 && (
                <button onClick={handleDeleteAll} style={{
                  fontSize: 11, color: T.textLight, background: "none", border: "none",
                  cursor: "pointer", fontFamily: "'Inter', 'DM Sans', sans-serif", fontWeight: 500,
                  padding: "2px 4px", borderRadius: 4,
                }}
                  title={t("notifications.clearAllTooltip")}
                  onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                  onMouseLeave={e => e.currentTarget.style.color = T.textLight}
                >{t("notifications.clearAll")}</button>
              )}
            </div>
          </div>

          {notifError && (
            <div style={{ margin: "10px 16px 0", fontSize: 12, color: "#c0392b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 10px" }}>
              {notifError}
            </div>
          )}

          {/* Notification list */}
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {notifs.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center", color: T.textLight, fontSize: 13 }}>
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "center", color: T.textXLight }}><BellOffIcon size={28} /></div>
                {t("notifications.emptyState")}
              </div>
            ) : notifs.map(n => {
              const isBroadcast = n.type === "platform_update";
              const unreadBg    = isBroadcast ? "#ede9fe66" : T.blueLight + "55";
              const isHovered   = hoveredId === n.id;
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  onMouseEnter={() => setHoveredId(n.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    padding: "10px 16px 10px 16px", cursor: "pointer", transition: "background 0.1s",
                    borderBottom: `1px solid ${T.border}`,
                    background: isHovered ? T.surfaceAlt : n.read ? T.surface : unreadBg,
                    borderLeft: isBroadcast ? "3px solid #7c3aed" : "3px solid transparent",
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    {isBroadcast ? (
                      <div style={{
                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                        background: "#ede9fe", border: "1px solid #c4b5fd44",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <BellIcon size={15} color="#7c3aed" />
                      </div>
                    ) : (
                      <span style={{ display: "flex", alignItems: "center", flexShrink: 0, color: T.textMid, paddingTop: 2 }}>
                        <NotifIcon type={n.type} size={18} />
                      </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isBroadcast && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 3 }}>
                          {t("notifications.platformLabel")}
                        </div>
                      )}
                      <div style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: T.text, marginBottom: 2 }}>
                        {n.title}
                      </div>
                      <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.4, marginBottom: 4 }}>
                        {/* Bell rows from the weekly candidate digest. It stopped creating them on 2026-10-28 (owner
                            decision); older rows still say "therapists", so show the provider wording. The last rows
                            expire by 2026-11-30, then remove this line and notifications.candidateDigestBody. */}
                        {n.type === "candidate_digest" ? t("notifications.candidateDigestBody") : n.body}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {n.linkCaseCode && (
                          <span style={{
                            fontSize: 10, fontWeight: 800, color: T.blue, background: T.blueLight,
                            border: `1px solid ${T.blue}44`, borderRadius: 5, padding: "1px 6px",
                            fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.03em",
                          }}>{n.linkCaseCode}</span>
                        )}
                        <span style={{ fontSize: 11, color: T.textLight, fontFamily: "'JetBrains Mono', monospace" }}>
                          {n.time}
                        </span>
                      </div>
                    </div>

                    {/* Per-item action buttons — visible on hover */}
                    <div style={{
                      display: "flex", gap: 2, alignItems: "center", flexShrink: 0,
                      opacity: isHovered ? 1 : 0,
                      transition: "opacity 0.12s",
                      pointerEvents: isHovered ? "auto" : "none",
                    }}>
                      {/* Toggle read/unread */}
                      <button
                        onClick={e => handleToggleRead(e, n)}
                        title={n.read ? t("notifications.markUnreadTooltip") : t("notifications.markReadTooltip")}
                        style={{
                          width: 26, height: 26, borderRadius: 6, border: "none",
                          background: "transparent", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: n.read ? T.textLight : T.blue,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = T.border}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {n.read ? (
                          /* unread dot icon — click to mark unread */
                          <div style={{ width: 8, height: 8, borderRadius: "50%", border: `2px solid ${T.textLight}` }} />
                        ) : (
                          /* filled dot — click to mark read */
                          <CheckIcon size={13} color={T.blue} />
                        )}
                      </button>

                      {/* Delete */}
                      <button
                        onClick={e => handleDelete(e, n)}
                        title={t("notifications.deleteTooltip")}
                        style={{
                          width: 26, height: 26, borderRadius: 6, border: "none",
                          background: "transparent", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: T.textLight,
                          transition: "background 0.1s, color 0.1s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#dc2626"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textLight; }}
                      >
                        <XIcon size={12} />
                      </button>
                    </div>

                    {/* Unread dot — hidden when action buttons are showing */}
                    {!n.read && !isHovered && (
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: isBroadcast ? "#7c3aed" : T.blue, flexShrink: 0, marginTop: 4 }} />
                    )}
                  </div>
                </div>
              );
            })}
            {hasMore && notifs.length > 0 && (
              <button onClick={loadOlder} disabled={loadingMore} style={{
                display: "block", width: "100%", padding: "10px 16px",
                fontSize: 12, fontWeight: 600, color: T.blue, background: "none", border: "none",
                cursor: loadingMore ? "default" : "pointer", opacity: loadingMore ? 0.6 : 1,
                fontFamily: "'Inter', 'DM Sans', sans-serif",
              }}>{loadingMore ? t("notifications.loadingOlder") : t("notifications.showOlder")}</button>
            )}
          </div>

          {/* Footer */}
          {(userRole === "rbt" || userRole === "bcba" || userRole === "bcaba") && (
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => { setView("notifications"); setOpen(false); }} style={{
                width: "100%", padding: "8px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                fontFamily: "'Inter', 'DM Sans', sans-serif", cursor: "pointer",
                border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.textMid,
                transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <SettingsIcon size={13} color={T.textMid} /> {t("notifications.settingsButton")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
